import { Action, ActionCreator } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { GlobalState } from "@/Reducers/interface";
import {
	Tag,
	TagBulkUpsertRequest,
	TagEntityType
} from "@/Contracts/Tags";
import {
	bulkUpsertTags as bulkUpsertTagsService,
	listTagsByEntityType
} from "@/Services/Tags/TagService";

export enum TagActionTypes {
	FETCH_TAGS_REQUESTED = "FETCH_TAGS_REQUESTED",
	FETCH_TAGS_SUCCESSFUL = "FETCH_TAGS_SUCCESSFUL",
	FETCH_TAGS_FAILED = "FETCH_TAGS_FAILED",

	BULK_UPSERT_TAGS_SUCCESSFUL = "BULK_UPSERT_TAGS_SUCCESSFUL"
}

export interface FetchTagsRequestedAction
	extends Action<TagActionTypes.FETCH_TAGS_REQUESTED> {
	readonly entityType: TagEntityType;
}

export interface FetchTagsSuccessfulAction
	extends Action<TagActionTypes.FETCH_TAGS_SUCCESSFUL> {
	readonly entityType: TagEntityType;
	readonly tags: Tag[];
}

export interface FetchTagsFailedAction
	extends Action<TagActionTypes.FETCH_TAGS_FAILED> {
	readonly entityType: TagEntityType;
	readonly error: string;
}

export interface BulkUpsertTagsSuccessfulAction
	extends Action<TagActionTypes.BULK_UPSERT_TAGS_SUCCESSFUL> {
	readonly entityType: TagEntityType;
	readonly tags: Tag[];
}

export type TagActions =
	| FetchTagsRequestedAction
	| FetchTagsSuccessfulAction
	| FetchTagsFailedAction
	| BulkUpsertTagsSuccessfulAction;

export interface TagDispatch {
	fetchTagsIfNotLoaded: ActionCreator<
		ThunkAction<
			Promise<FetchTagsSuccessfulAction | null>,
			GlobalState,
			unknown,
			TagActions
		>
	>;
	bulkUpsertTags: ActionCreator<
		ThunkAction<Promise<Tag[]>, GlobalState, unknown, TagActions>
	>;
}

export const TagActionCreators: TagDispatch = {
	fetchTagsIfNotLoaded:
		(entityType: TagEntityType) =>
		async (
			dispatch: ThunkDispatch<GlobalState, unknown, TagActions>,
			getState: () => GlobalState
		): Promise<FetchTagsSuccessfulAction | null> => {
			const bucket = getState().tags.byEntityType[entityType];
			if (bucket && (bucket.loading || bucket.loadedAtMs)) {
				return null;
			}
			const requestedAction: FetchTagsRequestedAction = {
				type: TagActionTypes.FETCH_TAGS_REQUESTED,
				entityType
			};
			dispatch(requestedAction);
			try {
				const tags = await listTagsByEntityType(entityType);
				const successfulAction: FetchTagsSuccessfulAction = {
					type: TagActionTypes.FETCH_TAGS_SUCCESSFUL,
					entityType,
					tags
				};
				return dispatch(successfulAction);
			} catch (error) {
				const failedAction: FetchTagsFailedAction = {
					type: TagActionTypes.FETCH_TAGS_FAILED,
					entityType,
					error:
						error instanceof Error
							? error.message
							: "Failed to fetch tags"
				};
				dispatch(failedAction);
				return Promise.reject(error);
			}
		},

	bulkUpsertTags:
		(request: TagBulkUpsertRequest) =>
		async (
			dispatch: ThunkDispatch<GlobalState, unknown, TagActions>
		): Promise<Tag[]> => {
			const tags = await bulkUpsertTagsService(request);
			const successfulAction: BulkUpsertTagsSuccessfulAction = {
				type: TagActionTypes.BULK_UPSERT_TAGS_SUCCESSFUL,
				entityType: request.entity_type,
				tags
			};
			dispatch(successfulAction);
			return tags;
		}
};

export default TagActionCreators;